import { useState } from "react";
import {
  Box,
  Text,
  Stack,
  Group,
  Button,
  Select,
  Switch,
  ActionIcon,
  Paper,
  Badge,
} from "@mantine/core";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconGripVertical, IconPlus, IconTrash } from "@tabler/icons-react";
import { TemplateSection, SectionType } from "../../../types/reportTemplate";

const ALL_SECTION_TYPES: Array<{ value: SectionType; label: string }> = [
  { value: "header", label: "Header" },
  { value: "patient_info", label: "Patient Information" },
  { value: "clinical_history", label: "Clinical History" },
  { value: "diagnosis", label: "Diagnosis" },
  { value: "comments", label: "Comments" },
  { value: "organ_map", label: "Organ Map" },
  { value: "diagnostic_map", label: "Diagnostic Map" },
  { value: "microscopic_description", label: "Microscopic Description" },
  { value: "gross_description", label: "Gross Description" },
  { value: "stain_table", label: "Stain Table" },
  { value: "signature_block", label: "Signature Block" },
  { value: "addendum", label: "Addendum" },
  { value: "cpt_icd_codes", label: "CPT / ICD Codes" },
  { value: "disclaimers", label: "Disclaimers" },
  { value: "quality_control_review", label: "Quality Control Review" },
];

interface SortableSectionItemProps {
  section: TemplateSection;
  index: number;
  onRemove: (index: number) => void;
  onUpdate: (index: number, updates: Partial<TemplateSection>) => void;
}

function SortableSectionItem({
  section,
  index,
  onRemove,
  onUpdate,
}: SortableSectionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `section-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const showPerSpecimen = [
    "diagnosis",
    "microscopic_description",
    "gross_description",
  ].includes(section.type);
  const showOrganMapType = ["organ_map", "diagnostic_map"].includes(
    section.type
  );
  const showCombined = [
    "microscopic_description",
    "gross_description",
  ].includes(section.type);
  const showHeading = ["diagnosis"].includes(section.type);

  return (
    <Paper ref={setNodeRef} style={style} p="sm" withBorder>
      <Group gap="xs" align="flex-start">
        <Box
          {...attributes}
          {...listeners}
          style={{ cursor: "grab", paddingTop: 4 }}
        >
          <IconGripVertical size={16} color="var(--mantine-color-gray-5)" />
        </Box>

        <Box style={{ flex: 1 }}>
          <Group justify="space-between" mb={4}>
            <Badge size="sm" variant="light">
              {ALL_SECTION_TYPES.find((t) => t.value === section.type)?.label ||
                section.type}
            </Badge>
            <ActionIcon
              size="sm"
              color="red"
              variant="subtle"
              onClick={() => onRemove(index)}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>

          {showPerSpecimen && (
            <Switch
              size="xs"
              label="Per specimen"
              checked={section.config?.perSpecimen ?? false}
              onChange={(e) =>
                onUpdate(index, {
                  config: {
                    ...section.config,
                    perSpecimen: e.currentTarget.checked,
                  },
                })
              }
              mt={4}
            />
          )}

          {showOrganMapType && (
            <Select
              size="xs"
              label="Organ map type"
              data={[
                { value: "upper-gi", label: "Upper GI" },
                { value: "prostate-biopsy", label: "Prostate Biopsy" },
              ]}
              value={section.config?.organMapType || "upper-gi"}
              onChange={(v) =>
                onUpdate(index, {
                  config: {
                    ...section.config,
                    organMapType: v as "upper-gi" | "prostate-biopsy",
                  },
                })
              }
              mt={4}
            />
          )}

          {showCombined && (
            <Switch
              size="xs"
              label="Combined (single field, not per-specimen)"
              checked={section.config?.combined ?? false}
              onChange={(e) =>
                onUpdate(index, {
                  config: {
                    ...section.config,
                    combined: e.currentTarget.checked,
                  },
                })
              }
              mt={4}
            />
          )}

          {showHeading && (
            <Switch
              size="xs"
              label="Show section heading"
              checked={section.config?.showHeading ?? true}
              onChange={(e) =>
                onUpdate(index, {
                  config: {
                    ...section.config,
                    showHeading: e.currentTarget.checked,
                  },
                })
              }
              mt={4}
            />
          )}
        </Box>
      </Group>
    </Paper>
  );
}

interface TemplateSectionEditorProps {
  sections: TemplateSection[];
  onChange: (sections: TemplateSection[]) => void;
}

export function TemplateSectionEditor({
  sections,
  onChange,
}: TemplateSectionEditorProps) {
  const [addType, setAddType] = useState<SectionType | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((_, i) => `section-${i}` === active.id);
    const newIndex = sections.findIndex((_, i) => `section-${i}` === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      onChange(arrayMove(sections, oldIndex, newIndex));
    }
  };

  const handleRemove = (index: number) => {
    onChange(sections.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, updates: Partial<TemplateSection>) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const handleAdd = () => {
    if (!addType) return;
    const typeLabel =
      ALL_SECTION_TYPES.find((t) => t.value === addType)?.label || addType;
    onChange([...sections, { type: addType, label: typeLabel.toUpperCase() }]);
    setAddType(null);
  };

  return (
    <Box>
      <Text size="sm" fw={600} mb={8}>
        Sections (drag to reorder)
      </Text>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sections.map((_, i) => `section-${i}`)}
          strategy={verticalListSortingStrategy}
        >
          <Stack gap={6}>
            {sections.map((section, index) => (
              <SortableSectionItem
                key={`${section.type}-${index}`}
                section={section}
                index={index}
                onRemove={handleRemove}
                onUpdate={handleUpdate}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      {sections.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No sections added yet. Add sections below.
        </Text>
      )}

      <Group mt={12} align="flex-end">
        <Select
          size="sm"
          label="Add section"
          placeholder="Select section type"
          data={ALL_SECTION_TYPES}
          value={addType}
          onChange={(v) => setAddType(v as SectionType | null)}
          searchable
          style={{ flex: 1 }}
        />
        <Button
          size="sm"
          leftSection={<IconPlus size={14} />}
          onClick={handleAdd}
          disabled={!addType}
        >
          Add
        </Button>
      </Group>
    </Box>
  );
}
